import { useSortBy } from 'react-instantsearch';
import { ptBrCommon } from '@/lib/pt-br/messages';
import { MAIN_SEARCH_INDEX_ID } from '@/lib/services/typesense/constants';
import { StarIcon } from '@/components/icons/star-icon';
import cn from 'classnames';

// When no sort has been explicitly chosen, react-instantsearch sets
// currentRefinement to the InstantSearch indexName (MAIN_SEARCH_INDEX_ID).
// We treat that state as "relevance is active" so the button appears selected
// both on the initial render and after navigating from another page.
const RELEVANCE_SORT_VALUE = `${MAIN_SEARCH_INDEX_ID}/sort/in_stock:desc,_text_match:desc,popularity_score:desc`;

type SortOption = {
  id: string;
  labelKey: string;
  value: string;
  icon: React.ReactNode;
};

const SORT_OPTIONS: SortOption[] = [
  {
    id: 'relevance',
    labelKey: 'text-sort-relevance',
    value: RELEVANCE_SORT_VALUE,
    icon: <StarIcon className="h-4 w-4 shrink-0" />,
  },
  {
    id: 'best-promotion',
    labelKey: 'text-sort-best-promotion',
    // discount_pct:desc → highest % off first; popularity_score as tiebreaker.
    value: `${MAIN_SEARCH_INDEX_ID}/sort/in_stock:desc,discount_pct:desc,popularity_score:desc`,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="19" y1="5" x2="5" y2="19" />
        <circle cx="6.5" cy="6.5" r="1.5" />
        <circle cx="17.5" cy="17.5" r="1.5" />
      </svg>
    ),
  },
  {
    id: 'price-asc',
    labelKey: 'text-sort-price-asc',
    value: `${MAIN_SEARCH_INDEX_ID}/sort/in_stock:desc,sale_price:asc,_text_match:desc`,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="12" y1="5" x2="12" y2="19" />
        <polyline points="19 12 12 19 5 12" />
      </svg>
    ),
  },
  {
    id: 'price-desc',
    labelKey: 'text-sort-price-desc',
    value: `${MAIN_SEARCH_INDEX_ID}/sort/in_stock:desc,sale_price:desc,_text_match:desc`,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="12" y1="19" x2="12" y2="5" />
        <polyline points="5 12 12 5 19 12" />
      </svg>
    ),
  },
];

const Sorting: React.FC = () => {
const { refine, currentRefinement } = useSortBy({
    items: [
      // Include the bare index as a valid item so InstantSearch
      // recognises the default state (no sort in URL) without warnings.
      { label: 'Default', value: MAIN_SEARCH_INDEX_ID },
      ...SORT_OPTIONS.map((o) => ({ label: o.id, value: o.value })),
    ],
  });

  function isOptionActive(option: SortOption): boolean {
    if (option.id === 'relevance') {
      // Active when explicitly selected OR when no sort has been applied yet
      // (currentRefinement falls back to the InstantSearch base indexName).
      return (
        currentRefinement === RELEVANCE_SORT_VALUE ||
        currentRefinement === MAIN_SEARCH_INDEX_ID
      );
    }
    return currentRefinement === option.value;
  }

  function handleToggle(option: SortOption) {
    if (option.id === 'relevance') {
      // Always apply the explicit relevance sort so the URL stays consistent.
      refine(RELEVANCE_SORT_VALUE);
    } else if (currentRefinement === option.value) {
      // Deselect price sort → back to bare index (relevance shows as active).
      refine(MAIN_SEARCH_INDEX_ID);
    } else {
      refine(option.value);
    }
  }

  return (
    <div className="flex flex-col gap-2.5">
      {SORT_OPTIONS.map((option) => {
        const isActive = isOptionActive(option);
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => handleToggle(option)}
            className={cn(
              'flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-opacity-50',
              isActive
                ? 'border-accent bg-accent text-white shadow-sm'
                : 'border-gray-200 bg-white text-body hover:border-accent hover:text-accent',
            )}
          >
            {option.icon}
            <span className="leading-none">{ptBrCommon(option.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
};

export default Sorting;
